import { useEffect, useMemo, useRef, useState } from 'react'
import { installAnalyticsDelegation, sendGoal, sendPageView } from './analytics.js'
import { COMPANY, CONTACTS, LEGAL_NAME, SITE_NAME } from './constants.js'
import { legalSnapshots } from './generated/legalSnapshots.js'
import { getLenaGuide, lenaMoments, payoutVideo } from './media.js'
import { getPage, normalizePath } from './siteData.js'
import { breadcrumbItems, updateDocumentMeta } from './seo.js'
import RegistrationChooser from './RegistrationChooser.jsx'
import AlexLanding from './AlexLanding.jsx'
import './styles.css'

function isModifiedClick(event) {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
}

function Link({ href, external = false, event, label, momentId, className = '', children, onNavigate, ...rest }) {
  const isExternal = external || /^https?:\/\//.test(href)
  const handleClick = (clickEvent) => {
    if (isExternal || href.startsWith('#') || isModifiedClick(clickEvent) || clickEvent.defaultPrevented) return
    clickEvent.preventDefault()
    onNavigate?.(href)
  }
  return (
    <a
      href={href}
      className={className}
      onClick={handleClick}
      data-analytics-event={event || undefined}
      data-analytics-label={label || undefined}
      data-analytics-moment-id={momentId || undefined}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
    >
      {children}
    </a>
  )
}

function LenaMoment({ moment, pagePath, onNavigate }) {
  if (!moment?.asset || !moment.title || !moment.text) return null
  const headingId = `lena-${moment.id}-heading`
  const event = moment.role === 'next' ? 'lena_next_step_click' : 'lena_checkpoint_click'
  return (
    <section className={`lena-moment lena-moment-${moment.role}`} aria-labelledby={headingId}>
      <figure>
        <img
          src={moment.asset.src}
          width={moment.asset.width}
          height={moment.asset.height}
          alt={moment.asset.alt}
          style={{ objectPosition: moment.asset.position }}
          loading="lazy"
          decoding="async"
        />
      </figure>
      <div className="lena-moment-copy">
        <p className="eyebrow">{moment.eyebrow}</p>
        <h2 id={headingId}>{moment.title}</h2>
        <p>{moment.text}</p>
        {moment.action && (
          <Link
            href={moment.action.href}
            event={event}
            label={`${pagePath}:${moment.action.label}`}
            momentId={moment.id}
            className="lena-moment-action"
            onNavigate={onNavigate}
          >
            <span>{moment.action.label}</span><Arrow />
          </Link>
        )}
      </div>
    </section>
  )
}

function Arrow() {
  return <span aria-hidden="true">↗</span>
}

function Header({ onNavigate }) {
  const nav = [
    ['/taxi', 'Водителям'],
    ['/dostavka', 'Курьерам'],
    ['/instructions', 'Инструкции'],
    ['/about', 'О парке'],
  ]
  return (
    <header className="site-header">
      <div className="header-rail">
        <Link href="/" onNavigate={onNavigate} className="brand" aria-label="ABG Taxi — на главную">
          <span className="brand-mark" aria-hidden="true">ABG</span>
          <span className="brand-name">Taxi</span>
        </Link>
        <nav aria-label="Основная навигация" className="main-nav">
          {nav.map(([href, text]) => (
            <Link key={href} href={href} onNavigate={onNavigate}>{text}</Link>
          ))}
        </nav>
        <Link href={CONTACTS.telegram} external event="telegram_click" label="header" className="header-help">
          Помощь <Arrow />
        </Link>
      </div>
    </header>
  )
}

function Breadcrumbs({ page, onNavigate }) {
  const crumbs = breadcrumbItems(page)
  if (!crumbs.length) return null
  return (
    <nav className="breadcrumbs" aria-label="Хлебные крошки">
      {crumbs.map((crumb, index) => (
        <span key={crumb.path}>
          {index < crumbs.length - 1 ? (
            <Link href={crumb.path} onNavigate={onNavigate}>{crumb.name}</Link>
          ) : <span aria-current="page">{crumb.name}</span>}
          {index < crumbs.length - 1 && <span aria-hidden="true"> / </span>}
        </span>
      ))}
    </nav>
  )
}

function Cta({ cta, onNavigate }) {
  const className = cta.kind === 'secondary' ? 'button button-secondary' : 'button button-primary'
  return (
    <Link
      href={cta.href}
      external={cta.external}
      event={cta.event}
      label={cta.label}
      className={className}
      onNavigate={onNavigate}
    >
      <span>{cta.label}</span><Arrow />
    </Link>
  )
}

function LenaGuide({ page, onNavigate }) {
  const guide = getLenaGuide(page.path)
  const content = (
    <>
      <img
        src={guide.asset.src}
        width={guide.asset.width}
        height={guide.asset.height}
        alt={guide.asset.alt}
        style={{ objectPosition: guide.asset.position }}
        loading="eager"
        fetchPriority="high"
      />
      <figcaption>
        <span className="lena-name">Лена · проводник ABG Taxi и Едару</span>
        <q>{guide.quote}</q>
        {guide.label && <span className="lena-action">{guide.label} <Arrow /></span>}
      </figcaption>
    </>
  )
  return (
    <figure className={`lena-guide lena-guide-${guide.variant || 'hero'}`}>
      {guide.href ? (
        <Link
          href={guide.href}
          event="lena_guide_click"
          label={`${page.path}:${guide.label}`}
          onNavigate={onNavigate}
        >
          {content}
        </Link>
      ) : content}
    </figure>
  )
}

function Hero({ page, onNavigate }) {
  return (
    <section className={`hero hero-${page.layout}${page.registration ? ' hero-registration' : ''}`}>
      <div className="hero-copy">
        <p className="eyebrow">{page.eyebrow}</p>
        <h1>{page.h1}</h1>
        <p className="hero-lead">{page.lead}</p>
        {page.updated && !page.registration && <p className="hero-updated">{page.updated}</p>}
        {page.registration && <RegistrationChooser key={page.path} />}
        {page.ctas?.length > 0 && (
          <div className="hero-actions">
            {page.ctas.map((cta) => <Cta key={`${cta.href}-${cta.label}`} cta={cta} onNavigate={onNavigate} />)}
          </div>
        )}
      </div>
      <LenaGuide page={page} onNavigate={onNavigate} />
    </section>
  )
}

function PayoutVideo() {
  const playSent = useRef(false)
  const handlePlay = () => {
    if (playSent.current) return
    playSent.current = true
    sendGoal('payout_video_play', { page: window.location.pathname })
  }
  const handleEnded = () => sendGoal('payout_video_complete', { page: window.location.pathname })
  return (
    <section className="payout-video-section" id="payout-video" aria-labelledby="payout-video-heading">
      <div className="section-heading">
        <p className="eyebrow">Лена показывает на экране · 3:06</p>
        <h2 id="payout-video-heading">Как вывести деньги из Яндекс Про</h2>
        <p>Интерфейс может отличаться по городу и типу подключения. Сначала проверьте, что деньги уже появились на балансе, а затем повторяйте шаги из видео.</p>
        <Link
          href={CONTACTS.telegram}
          external
          event="payout_help_click"
          label="payout-video"
          className="video-help-link"
        >
          Не получается вывести — написать парку <Arrow />
        </Link>
      </div>
      <div className="video-frame">
        <video
          controls
          playsInline
          preload="metadata"
          poster={payoutVideo.poster}
          width={payoutVideo.width}
          height={payoutVideo.height}
          onPlay={handlePlay}
          onEnded={handleEnded}
        >
          <source src={payoutVideo.src} type="video/mp4" />
          Ваш браузер не смог открыть видео. <a href={payoutVideo.src}>Скачайте MP4</a>.
        </video>
        <p className="video-fallbacks">
          Если видео не запускается: <a href={payoutVideo.youtube} target="_blank" rel="noopener noreferrer">YouTube</a>
          {' · '}
          <a href={payoutVideo.vk} target="_blank" rel="noopener noreferrer">VK Видео</a>
        </p>
      </div>
    </section>
  )
}

function RouteChooser({ routes, onNavigate }) {
  return (
    <section className="route-chooser" aria-labelledby="route-heading">
      <div className="section-heading">
        <p className="eyebrow">Один выбор — один следующий шаг</p>
        <h2 id="route-heading">Куда идём?</h2>
      </div>
      <div className="route-list">
        {routes.map((route) => (
          <Link
            key={route.href}
            href={route.href}
            event={route.event}
            label={route.title}
            className="route-row"
            onNavigate={onNavigate}
          >
            <span className="route-number">{route.number}</span>
            <span className="route-copy">
              <strong>{route.title}</strong>
              <span>{route.text}</span>
            </span>
            <span className="route-action">{route.action} <Arrow /></span>
          </Link>
        ))}
      </div>
    </section>
  )
}

function Facts({ facts }) {
  if (!facts?.length) return null
  return (
    <section className="fact-rail" aria-label="Ключевые факты">
      {facts.map(([label, value]) => (
        <div className="fact" key={`${label}-${value}`}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </section>
  )
}

function Steps({ steps }) {
  return (
    <ol className="step-list">
      {steps.map((step, index) => {
        const [title, text] = Array.isArray(step) ? step : [step, null]
        return (
          <li key={`${title}-${index}`}>
            <span className="step-index">{String(index + 1).padStart(2, '0')}</span>
            <div><strong>{title}</strong>{text && <p>{text}</p>}</div>
          </li>
        )
      })}
    </ol>
  )
}

function ItemRows({ items }) {
  return (
    <div className="item-rows">
      {items.map(([title, text], index) => (
        <article key={title}>
          <span className="item-dot" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
          <h3>{title}</h3>
          <p>{text}</p>
        </article>
      ))}
    </div>
  )
}

function ContentSections({ page, onNavigate }) {
  const blocks = []
  if (page.steps) blocks.push({ heading: 'Как это работает', steps: page.steps })
  if (page.sections) blocks.push(...page.sections)
  return blocks.map((section, index) => (
    <section className="content-section" id={section.anchor} key={`${section.heading}-${index}`}>
      <div className="section-heading">
        <p className="eyebrow">{String(index + 1).padStart(2, '0')} / По делу</p>
        <h2>{section.heading}</h2>
      </div>
      <div className="section-content">
        {section.lead && <p className="section-lead">{section.lead}</p>}
        {section.steps && <Steps steps={section.steps} />}
        {section.items && <ItemRows items={section.items} />}
        {section.note && <p className="section-note">{section.note}</p>}
        {section.source && (
          <Link
            href={section.source.href}
            external
            className="section-source"
            onNavigate={onNavigate}
          >
            {section.source.label} <Arrow />
          </Link>
        )}
      </div>
    </section>
  ))
}

function Instructions({ instructions, eyebrow = 'Выберите ситуацию', heading = 'Что случилось?', onNavigate }) {
  if (!instructions?.length) return null
  return (
    <section className="content-section">
      <div className="section-heading">
        <p className="eyebrow">{eyebrow}</p>
        <h2>{heading}</h2>
      </div>
      <div className="instruction-list">
        {instructions.map((item) => (
          <Link
            key={item.number}
            href={item.href}
            external={item.external}
            event={item.event || 'instruction_open'}
            label={item.title}
            onNavigate={onNavigate}
          >
            <span>{item.number}</span>
            <strong>{item.title}</strong>
            <p>{item.text}</p>
            <Arrow />
          </Link>
        ))}
      </div>
    </section>
  )
}

function NextSteps({ items, onNavigate }) {
  if (!items?.length) return null
  return (
    <section className="next-section">
      <p className="eyebrow">Следующий шаг</p>
      <h2>Продолжить по своей ситуации</h2>
      <div className="next-links">
        {items.map(([href, label, event]) => (
          <Link key={`${href}-${label}`} href={href} event={event} external={/^https?:/.test(href)} onNavigate={onNavigate}>
            <span>{label}</span><Arrow />
          </Link>
        ))}
      </div>
    </section>
  )
}

function Faq({ items }) {
  if (!items?.length) return null
  return (
    <section className="faq-section">
      <div className="section-heading">
        <p className="eyebrow">Короткие ответы</p>
        <h2>Частые вопросы</h2>
      </div>
      <div className="faq-list">
        {items.map(([question, answer]) => (
          <details key={question}>
            <summary>{question}<span aria-hidden="true">+</span></summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

function LegalDocument({ page }) {
  const document = legalSnapshots[page.legalKey]
  const paragraphs = document.text.split(/\n{2,}/).filter(Boolean)
  return (
    <section className="legal-document">
      <div className="legal-text">
        {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
      </div>
    </section>
  )
}

function Footer({ onNavigate }) {
  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div>
          <p className="eyebrow">Оператор сайта</p>
          <strong>{LEGAL_NAME}</strong>
          <p>ИНН {COMPANY.inn} · ОГРН {COMPANY.ogrn}</p>
          <p>{COMPANY.address}</p>
        </div>
        <div className="footer-links">
          <Link href="/offer" onNavigate={onNavigate}>Оферта</Link>
          <Link href="/privacy" onNavigate={onNavigate}>Политика данных</Link>
          <Link href="/about" onNavigate={onNavigate}>Контакты</Link>
          <Link href={CONTACTS.phoneHref} external>{CONTACTS.phoneDisplay}</Link>
        </div>
      </div>
      <div className="footer-note">
        <span>© {new Date().getFullYear()} {SITE_NAME}</span>
        <span>Сайт партнёра. Не является сайтом Яндекса.</span>
      </div>
    </footer>
  )
}

function NotFound({ onNavigate }) {
  return (
    <main className="not-found">
      <p className="eyebrow">Ошибка 404</p>
      <h1>Такого маршрута нет</h1>
      <p>Вернитесь к выбору водителя или курьера — там есть актуальные варианты и инструкции.</p>
      <Link href="/" onNavigate={onNavigate} className="button button-primary">На главную <Arrow /></Link>
    </main>
  )
}

export default function App({ url = '/' }) {
  const initial = normalizePath(url)
  const [path, setPath] = useState(initial)
  const firstPageView = useRef(true)
  const page = useMemo(() => getPage(path), [path])

  const navigate = (href) => {
    const next = normalizePath(href)
    if (next === path) {
      const hash = href.includes('#') ? `#${href.split('#')[1]}` : ''
      if (!hash) return
      window.history.pushState({}, '', href)
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    window.history.pushState({}, '', href)
    setPath(next)
  }

  useEffect(() => {
    const handlePopState = () => setPath(normalizePath(window.location.pathname))
    window.addEventListener('popstate', handlePopState)
    const removeAnalytics = installAnalyticsDelegation()
    return () => {
      window.removeEventListener('popstate', handlePopState)
      removeAnalytics()
    }
  }, [])

  useEffect(() => {
    if (!page) return
    updateDocumentMeta(page)
    const hash = window.location.hash
    window.requestAnimationFrame(() => {
      if (hash) document.querySelector(hash)?.scrollIntoView({ behavior: 'instant', block: 'start' })
      else window.scrollTo({ top: 0, behavior: 'instant' })
    })
    if (firstPageView.current) {
      firstPageView.current = false
    } else {
      sendPageView(window.location.pathname + window.location.search, page.title)
    }
  }, [page])

  if (!page) return <><Header onNavigate={navigate} /><NotFound onNavigate={navigate} /><Footer onNavigate={navigate} /></>

  return (
    <>
      <Header onNavigate={navigate} />
      <main>
        <div className="page-shell">
          <Breadcrumbs page={page} onNavigate={navigate} />
          {page.path === '/alex' ? <AlexLanding /> : <Hero page={page} onNavigate={navigate} />}
          {page.routes && <RouteChooser routes={page.routes} onNavigate={navigate} />}
          <Facts facts={page.facts} />
          {page.path !== '/alex' && lenaMoments[page.path]?.checkpoint && (
            <LenaMoment moment={lenaMoments[page.path].checkpoint} pagePath={page.path} onNavigate={navigate} />
          )}
          {page.path === '/instructions/yandex-pro' && <PayoutVideo />}
          {page.layout === 'legal' ? <LegalDocument page={page} /> : (
            <>
              <Instructions
                instructions={page.instructions}
                eyebrow={page.instructionsEyebrow}
                heading={page.instructionsHeading}
                onNavigate={navigate}
              />
              <ContentSections page={page} onNavigate={navigate} />
              {page.path !== '/alex' && <LenaMoment moment={lenaMoments[page.path]?.next} pagePath={page.path} onNavigate={navigate} />}
              <NextSteps items={page.next} onNavigate={navigate} />
              <Faq items={page.faq} />
            </>
          )}
        </div>
      </main>
      <Footer onNavigate={navigate} />
    </>
  )
}
